/**
 * 智慧內容正規化工具
 *
 * 處理各種來源的小說內容格式，統一輸出段落陣列
 * 支援：爬蟲內容、用戶上傳、各種換行/縮排格式
 */

export interface NormalizedContent {
  /** 分割後的段落陣列 */
  paragraphs: string[];
  /** 內容是否已有全形空格縮排 */
  hasIndent: boolean;
}

/**
 * 智慧段落分割與縮排偵測
 *
 * 分割優先順序：
 * 1. 雙換行 (\n\n) - 標準段落格式
 * 2. 全形縮排模式 - 中文小說常見 (\u3000\u3000開頭)
 * 3. 單換行 (\n) - fallback
 * 4. 連續全形空格 - 極端 fallback
 *
 * @param content 原始章節內容
 * @returns 正規化後的內容，包含段落陣列和縮排資訊
 */
export function normalizeContent(content: string): NormalizedContent {
  if (!content?.trim()) {
    return { paragraphs: [], hasIndent: false };
  }

  const trimmed = content.trim();

  // 偵測是否已有全形空格縮排（一個或兩個全形空格開頭）
  // \u3000 是全形空格，\u3000 也是全形空格
  const hasIndent =
    /^[\u3000]{1,2}/.test(trimmed) || /\n[\u3000]{1,2}/.test(trimmed);

  let paragraphs: string[];

  // 1. 先嘗試雙換行分割 (標準格式)
  if (trimmed.includes('\n\n')) {
    paragraphs = trimmed
      .split(/\n\n+/)
      .map((p) => p.trim())
      .filter((p) => p);
  }
  // 2. 偵測全形縮排模式並分割
  else if (hasIndent) {
    // 以「換行後緊接全形空格」作為段落分隔點
    paragraphs = trimmed
      .split(/\n(?=[\u3000]{1,2})/)
      .map((p) => p.trim())
      .filter((p) => p);
  }
  // 3. 單換行分割 (fallback)
  else if (trimmed.includes('\n')) {
    paragraphs = trimmed
      .split(/\n+/)
      .map((p) => p.trim())
      .filter((p) => p);
  }
  // 4. 無換行時，嘗試用連續全形空格分割
  else if (/[\u3000]{2,}/.test(trimmed)) {
    paragraphs = trimmed.split(/[\u3000]{2,}/).filter((p) => p.trim());
  }
  // 5. 返回整段
  else {
    paragraphs = [trimmed];
  }

  return { paragraphs, hasIndent };
}
